import type { CardDefinition, CardId, ContentSnapshot } from '../../engine/types';
import { SeededRng } from '../../engine/rng';
import { DECK_SIZE, isValidDeckComposition } from '../../engine/deck-rules';
import type { PlayerLearningModel, PlayerStyleVector } from '../learning/types';
import { dominantPlayerStyle } from '../learning/types';
import type { BotDeckCandidateSummary, BotDeckSelection } from '../core/types';
import { compareStableText, compareStableTextArrays } from '../../engine/stable-order';

export const ADAPTIVE_DECK_POLICY_ID = 'adaptive-counter-deck';
export const ADAPTIVE_DECK_POLICY_VERSION = '1.0.0';
export const MANUAL_DECK_POLICY_ID = 'manual-deck';
export const MANUAL_DECK_POLICY_VERSION = '1.0.0';

const roleCount = (cards: readonly CardDefinition[], role: CardDefinition['roles'][number]): number => cards.filter((card) => card.roles.includes(role)).length;

function cardHasAirUnit(card: CardDefinition, content: ContentSnapshot): boolean {
  return card.mechanics.some((mechanic) => mechanic.type === 'spawn-units' && mechanic.groups.some((group) => content.units.get(group.unitId)?.movement === 'air'));
}

function deckRoleSignature(cards: readonly CardDefinition[]): string {
  const roles = ['tank', 'ranged', 'splash', 'swarm', 'anti-air', 'defense', 'spell'] as const;
  const rolePart = roles.map((role) => `${role}:${Math.min(3, roleCount(cards, role))}`).join('|');
  const averageCost = cards.reduce((sum, card) => sum + card.cost, 0) / cards.length;
  return `${rolePart}|cost:${Math.round(averageCost)}`;
}

export function strategyOutcomeKey(style: PlayerStyleVector, cards: readonly CardDefinition[]): string {
  return `${dominantPlayerStyle(style)}::${deckRoleSignature(cards)}`;
}

function hasRequiredCoverage(cards: readonly CardDefinition[]): boolean {
  const averageCost = cards.reduce((sum, card) => sum + card.cost, 0) / cards.length;
  return roleCount(cards, 'tank') >= 1
    && roleCount(cards, 'tank') <= 2
    && (roleCount(cards, 'anti-air') >= 1 || roleCount(cards, 'ranged') >= 2)
    && roleCount(cards, 'splash') >= 1
    && roleCount(cards, 'defense') >= 1
    && roleCount(cards, 'spell') >= 1
    && roleCount(cards, 'spell') <= 2
    && cards.filter((card) => card.cost >= 6).length <= 2
    && averageCost >= 2.75
    && averageCost <= 4.75;
}

function counterScore(cards: readonly CardDefinition[], style: PlayerStyleVector, content: ContentSnapshot): number {
  const defense = Math.min(4, roleCount(cards, 'defense'));
  const antiAir = Math.min(6, roleCount(cards, 'anti-air') + cards.filter((card) => cardHasAirUnit(card, content)).length);
  const splash = Math.min(6, roleCount(cards, 'splash'));
  const tanks = Math.min(2, roleCount(cards, 'tank'));
  const spells = Math.min(2, roleCount(cards, 'spell'));
  const ranged = Math.min(3, roleCount(cards, 'ranged'));
  return Math.round((
    style.aggression * defense * 22
    + style.beatdown * (defense * 24 + ranged * 8)
    + style.airPressure * antiAir * 28
    + style.swarm * splash * 30
    + style.cycle * (splash * 12 + defense * 8)
    + style.spellControl * (tanks * 14 + spells * 6)
    + style.defense * (tanks * 18 + spells * 10)
  ) / 1_000);
}

function coverageScore(cards: readonly CardDefinition[], content: ContentSnapshot): number {
  const roleCoverage = ['tank', 'ranged', 'splash', 'swarm', 'anti-air', 'defense', 'spell']
    .filter((role) => cards.some((card) => card.roles.includes(role as CardDefinition['roles'][number]))).length;
  const airOptions = cards.filter((card) => cardHasAirUnit(card, content)).length;
  return roleCoverage * 90 + Math.min(2, airOptions) * 35;
}

function synergyScore(cards: readonly CardDefinition[]): number {
  const averageCostMilli = Math.round(cards.reduce((sum, card) => sum + card.cost * 1_000, 0) / cards.length);
  const costPenalty = Math.abs(3_600 - averageCostMilli) / 8;
  const support = roleCount(cards, 'tank') * (roleCount(cards, 'ranged') + roleCount(cards, 'splash')) * 30;
  const cycle = cards.filter((card) => card.cost <= 3).length * 16;
  const duplicateRolePenalty = Math.max(0, roleCount(cards, 'tank') - 2) * 100
    + Math.max(0, roleCount(cards, 'spell') - 2) * 90
    + Math.max(0, roleCount(cards, 'splash') - 3) * 85
    + Math.max(0, roleCount(cards, 'anti-air') - 3) * 55
    + Math.max(0, roleCount(cards, 'ranged') - 4) * 45;
  return Math.round(520 + support + cycle - costPenalty - duplicateRolePenalty);
}

function outcomeScore(cards: readonly CardDefinition[], model: PlayerLearningModel): number {
  const outcome = model.strategyOutcomes[strategyOutcomeKey(model.style, cards)];
  if (!outcome) return 0;
  const total = outcome.wins + outcome.losses + outcome.draws;
  if (!total) return 0;
  return Math.round(((outcome.wins + outcome.draws * 0.4) / total - 0.5) * 400);
}

function enumerateDecks(cards: readonly CardDefinition[]): CardDefinition[][] {
  const result: CardDefinition[][] = [];
  const selected: CardDefinition[] = [];
  const visit = (start: number): void => {
    if (selected.length === DECK_SIZE) {
      if (hasRequiredCoverage(selected)) result.push([...selected]);
      return;
    }
    const needed = DECK_SIZE - selected.length;
    for (let index = start; index <= cards.length - needed; index += 1) {
      selected.push(cards[index]!);
      visit(index + 1);
      selected.pop();
    }
  };
  visit(0);
  return result;
}

function summary(cards: readonly CardDefinition[], model: PlayerLearningModel, content: ContentSnapshot): BotDeckCandidateSummary {
  const counter = counterScore(cards, model.style, content);
  const synergy = synergyScore(cards);
  const coverage = coverageScore(cards, content);
  const outcome = outcomeScore(cards, model);
  return {
    deck: cards.map((card) => card.id),
    score: counter * 12 + synergy * 2 + coverage + outcome,
    counterScore: counter,
    synergyScore: synergy,
    coverageScore: coverage,
    outcomeScore: outcome,
  };
}

export function selectManualDeck(manualDeck: readonly CardId[]): BotDeckSelection {
  if (!isValidDeckComposition(manualDeck)) throw new Error('Manual bot deck must contain exactly eight unique cards');
  return { deck: [...manualDeck], score: 0, evaluatedCount: 1, topCandidates: [], rationale: ['manual-deck'] };
}

export function selectAdaptiveDeck(content: ContentSnapshot, model: PlayerLearningModel, seed: number, fallback: readonly CardId[]): BotDeckSelection {
  if (model.matchCount === 0 || model.confidence === 0) return { ...selectManualDeck(fallback), rationale: ['insufficient-learning-history', 'manual-fallback'] };
  const cards = [...content.cards.values()].sort((left, right) => compareStableText(left.id, right.id));
  const decks = enumerateDecks(cards);
  const candidates = decks.map((deck) => summary(deck, model, content)).sort((left, right) => right.score - left.score || compareStableTextArrays(left.deck, right.deck));
  if (!candidates.length) return { ...selectManualDeck(fallback), rationale: ['no-valid-adaptive-deck', 'manual-fallback'] };
  const bestScore = candidates[0]!.score;
  const nearBest = candidates.filter((candidate) => bestScore - candidate.score <= 80).slice(0, 3);
  const rng = new SeededRng((seed ^ model.revision ^ 0x5f3759df) >>> 0);
  const selected = nearBest[rng.integer(0, nearBest.length - 1)]!;
  return {
    deck: selected.deck,
    score: selected.score,
    evaluatedCount: candidates.length,
    topCandidates: candidates.slice(0, 3),
    rationale: [`counter-${dominantPlayerStyle(model.style)}`, `confidence-${model.confidence}`, nearBest.length > 1 ? 'seeded-top-variation' : 'best-score'],
  };
}

export function botDeckStrategySignature(content: ContentSnapshot, deck: readonly CardId[]): string {
  const cards = deck.map((cardId) => content.cards.get(cardId)).filter((card): card is CardDefinition => Boolean(card));
  return deckRoleSignature(cards);
}
